import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from 'i18n';
import {
  CommunityTranslationBatchResponse,
  CommunityTranslationItem,
  CommunityTranslationReason,
  CommunityTranslationViewStatus,
  communitySameLocale
} from '../models/community.models';
import { CommunityService } from './community.service';
import { CommunityTranslationPreferenceService } from './community-translation-preference.service';

export type CommunityTranslationKind = 'thread' | 'post';

/** One translatable element on the page: the thread itself, or one post. */
export interface CommunityTranslationTarget {
  kind: CommunityTranslationKind;
  id: number;
  /** The language the element was written in; without it nothing is offered. */
  sourceLocale: string;
}

/** What the toggle component renders for one element. */
export interface CommunityTranslationEntry extends CommunityTranslationTarget {
  status: CommunityTranslationViewStatus;
  /** Thread only. Plain text. */
  title?: string;
  /** Sanitized HTML fragment. */
  text?: string;
  reason?: CommunityTranslationReason;
  /** Whether the translation (rather than the original) is on screen. */
  showing: boolean;
  /**
   * Whether the viewer (or their `always` preference) asked for this element.
   * A translation that merely arrives — the collective endpoint answers for the
   * thread on every call — must not replace a body nobody asked to have swapped.
   */
  requested: boolean;
  /** When the current `pending` started, for the two-minute cap. */
  pendingSince?: number;
}

/** Delay in seconds after the server's own `retry_after`, per §3.2. */
const POLL_BACKOFF_SECONDS = [3, 5, 8];
/** After this long an element still `pending` is shown as `timeout`. */
const POLL_MAX_MS = 120_000;

function keyOf(kind: CommunityTranslationKind, id: number): string {
  return `${kind}:${id}`;
}

/**
 * Per-thread-page translation coordinator (§3.2 of docs/community/TRANSLATION.md).
 *
 * Provided by `CommunityThreadDetailPageComponent` (`providers: [...]`), never in
 * root: its whole state is one page's worth of elements, and a new page must
 * start empty. The translate toggles inject it `{ optional: true }` so they keep
 * rendering bodies on pages that do not provide one.
 *
 * The point of it existing at all is that the page must ask *once* for the whole
 * thread rather than once per post: one collective call, then polling with only
 * the ids that are still pending.
 */
@Injectable()
export class CommunityThreadTranslationService {
  private readonly community = inject(CommunityService);
  private readonly prefs = inject(CommunityTranslationPreferenceService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  readonly entries = signal<Record<string, CommunityTranslationEntry>>({});
  /** True while the provider is configured; nothing is requested without it. */
  private available = false;
  private threadId: number | null = null;
  private targets = new Map<string, CommunityTranslationTarget>();
  private uiLocale: string;

  private pollHandle: ReturnType<typeof setTimeout> | null = null;
  private pollAttempt = 0;
  private inFlight = false;

  constructor() {
    this.uiLocale = this.i18n.getLanguage();

    // `currentLanguage$` is a BehaviorSubject, so it replays the current value on
    // subscribe — compare before acting, or every page load would reset itself.
    this.i18n.currentLanguage$.pipe(takeUntilDestroyed()).subscribe(lang => {
      if (!lang || communitySameLocale(lang, this.uiLocale)) return;
      this.uiLocale = lang;
      this.onLanguageChanged();
    });

    this.prefs.modeChanges.pipe(takeUntilDestroyed()).subscribe(change => {
      this.onModeChanged(change.sourceLocale, change.mode);
    });

    this.destroyRef.onDestroy(() => this.cancelPoll());
  }

  /** Called by the page as soon as the thread id and `translation_available` are known. */
  configure(threadId: number, available: boolean): void {
    if (this.threadId !== null && this.threadId !== threadId) this.reset();
    this.threadId = threadId;
    this.available = available;
  }

  /**
   * Hand over the full list of elements currently on the page. Called after the
   * first load and again after every reload (a reply adds posts); only elements
   * not seen before are considered for the automatic pass, so a reload never
   * re-requests — or re-shows — what the viewer already toggled.
   */
  sync(targets: CommunityTranslationTarget[]): void {
    const seen = new Set<string>();
    const fresh: CommunityTranslationTarget[] = [];
    targets.forEach(target => {
      const key = keyOf(target.kind, target.id);
      seen.add(key);
      if (!this.targets.has(key)) fresh.push(target);
      this.targets.set(key, target);
    });

    // Drop what is no longer on the page (a deleted post), so polling cannot
    // keep asking for it.
    Array.from(this.targets.keys())
      .filter(key => !seen.has(key))
      .forEach(key => {
        this.targets.delete(key);
        this.entries.update(map => {
          const next = { ...map };
          delete next[key];
          return next;
        });
      });

    // Only the automatic pass runs from here: a fresh element is translated
    // unasked solely when its language is set to `always`. Everything else waits
    // for the viewer's click (bug on 2026-09-13: every fresh element was
    // requested and swapped in, which auto-translated whole threads for members
    // who had never asked).
    const auto = fresh.filter(t => this.isAuto(t));
    if (auto.length) this.request(auto, false);
  }

  /**
   * The automatic pass: every element whose language is set to `always` and that
   * is not already asked for. Idempotent, so the page may call it after `sync()`
   * (which already picks up new elements) without sending the same call twice.
   */
  requestAuto(): void {
    const targets = Array.from(this.targets.values()).filter(t => {
      if (!this.isAuto(t)) return false;
      const status = this.entryFor(t.kind, t.id)?.status;
      return status !== 'pending' && status !== 'ready';
    });
    if (targets.length) this.request(targets, false);
  }

  entryFor(kind: CommunityTranslationKind, id: number): CommunityTranslationEntry | null {
    return this.entries()[keyOf(kind, id)] ?? null;
  }

  /** "Translate once" — a single element, no retry flag. */
  requestOne(kind: CommunityTranslationKind, id: number): void {
    const target = this.targets.get(keyOf(kind, id));
    if (!target) return;
    const entry = this.entryFor(kind, id);
    if (entry?.status === 'ready') {
      this.setShowing(kind, id, true);
      return;
    }
    this.request([target], false);
  }

  /** "Try again" after a failure: same call, but the server clears its marker first. */
  retry(kind: CommunityTranslationKind, id: number): void {
    const target = this.targets.get(keyOf(kind, id));
    if (target) this.request([target], true);
  }

  /** Flip between translation and original; lossless, both are kept client-side. */
  toggleShowing(kind: CommunityTranslationKind, id: number): void {
    const entry = this.entryFor(kind, id);
    if (!entry || entry.status !== 'ready') return;
    this.setShowing(kind, id, !entry.showing);
  }

  // --- Internals ---------------------------------------------------------------

  private isAuto(target: CommunityTranslationTarget): boolean {
    return this.prefs.shouldAuto(target.sourceLocale, this.uiLocale, this.available);
  }

  private setShowing(kind: CommunityTranslationKind, id: number, showing: boolean): void {
    const key = keyOf(kind, id);
    this.entries.update(map => {
      const entry = map[key];
      if (!entry) return map;
      return { ...map, [key]: { ...entry, showing } };
    });
  }

  /**
   * One collective call for `targets`. Elements are marked `pending` up front so
   * the spinner appears immediately rather than after the round trip.
   */
  private request(targets: CommunityTranslationTarget[], retry: boolean): void {
    if (!this.available || this.threadId == null) return;
    const wanted = targets.filter(t => !communitySameLocale(t.sourceLocale, this.uiLocale));
    if (!wanted.length) return;

    const locale = this.uiLocale;
    const requestedThread = wanted.some(t => t.kind === 'thread');
    const postIds = wanted.filter(t => t.kind === 'post').map(t => t.id);
    const now = Date.now();

    this.entries.update(map => {
      const next = { ...map };
      wanted.forEach(target => {
        const key = keyOf(target.kind, target.id);
        const existing = next[key];
        if (existing?.status === 'ready' && !retry) {
          next[key] = { ...existing, requested: true, showing: true };
          return;
        }
        next[key] = {
          ...target,
          status: 'pending',
          showing: false,
          requested: true,
          pendingSince: now
        };
      });
      return next;
    });

    this.pollAttempt = 0;
    this.send(locale, requestedThread, postIds, retry);
  }

  private send(locale: string, includeThread: boolean, postIds: number[], retry: boolean): void {
    if (this.threadId == null) return;
    if (!includeThread && !postIds.length) return;
    this.inFlight = true;
    this.community.translateBatch(this.threadId, locale, postIds, retry).subscribe({
      next: res => {
        this.inFlight = false;
        // A language switch while the call was out makes the answer worthless.
        if (!communitySameLocale(locale, this.uiLocale)) return;
        this.apply(res, includeThread, postIds);
      },
      error: () => {
        this.inFlight = false;
        // Network/permission failure: show it as a failure the viewer can retry,
        // rather than a spinner that never ends.
        this.failPending(includeThread, postIds, 'error');
      }
    });
  }

  private apply(res: CommunityTranslationBatchResponse, includeThread: boolean, postIds: number[]): void {
    if (res.available === false) {
      this.available = false;
      this.cancelPoll();
      this.entries.set({});
      return;
    }

    this.entries.update(map => {
      const next = { ...map };
      if (res.thread && (includeThread || next[keyOf('thread', this.threadId ?? 0)])) {
        const key = keyOf('thread', this.threadId ?? 0);
        const merged = this.merge(next[key], 'thread', this.threadId ?? 0, res.thread);
        if (merged) next[key] = merged;
      }
      Object.entries(res.posts ?? {}).forEach(([rawId, item]) => {
        const id = Number(rawId);
        if (!id) return;
        const key = keyOf('post', id);
        if (!next[key] && !postIds.includes(id)) return;
        const merged = this.merge(next[key], 'post', id, item);
        if (merged) next[key] = merged;
      });
      return next;
    });

    this.scheduleNextPoll(res.retry_after);
  }

  /** Fold one server item into the entry, keeping `showing`/`requested` intact. */
  private merge(
    existing: CommunityTranslationEntry | undefined,
    kind: CommunityTranslationKind,
    id: number,
    item: CommunityTranslationItem
  ): CommunityTranslationEntry | null {
    const target = this.targets.get(keyOf(kind, id)) ?? existing;
    if (!target) return null;
    const base: CommunityTranslationEntry = existing ?? {
      kind,
      id,
      sourceLocale: target.sourceLocale,
      status: 'pending',
      showing: false,
      requested: false
    };
    const status = item.status;
    if (status === 'ready') {
      return {
        ...base,
        status,
        title: item.title,
        text: item.text,
        reason: undefined,
        pendingSince: undefined,
        // Only swap the body in for something that was actually asked for.
        showing: base.requested ? true : base.showing
      };
    }
    if (status === 'pending') {
      return { ...base, status, reason: undefined, pendingSince: base.pendingSince ?? Date.now() };
    }
    return { ...base, status, reason: item.reason, pendingSince: undefined, showing: false };
  }

  /** Turn the elements of a failed call into a retryable failure. */
  private failPending(includeThread: boolean, postIds: number[], reason: CommunityTranslationReason): void {
    const keys = [
      ...(includeThread && this.threadId != null ? [keyOf('thread', this.threadId)] : []),
      ...postIds.map(id => keyOf('post', id))
    ];
    this.entries.update(map => {
      const next = { ...map };
      keys.forEach(key => {
        const entry = next[key];
        if (entry && entry.status === 'pending') {
          next[key] = { ...entry, status: 'failed', reason, pendingSince: undefined, showing: false };
        }
      });
      return next;
    });
  }

  // --- Polling -------------------------------------------------------------------

  private scheduleNextPoll(serverRetryAfter?: number): void {
    this.expireTimeouts();
    const pending = this.pendingEntries();
    if (!pending.length) {
      this.cancelPoll();
      return;
    }
    if (this.pollHandle || this.inFlight) return;

    const seconds = this.pollAttempt === 0
      ? Math.max(1, Number(serverRetryAfter) || 2)
      : POLL_BACKOFF_SECONDS[Math.min(this.pollAttempt - 1, POLL_BACKOFF_SECONDS.length - 1)];
    this.pollAttempt += 1;

    this.pollHandle = setTimeout(() => {
      this.pollHandle = null;
      this.poll();
    }, seconds * 1000);
  }

  private poll(): void {
    this.expireTimeouts();
    const pending = this.pendingEntries();
    if (!pending.length || this.threadId == null) return;
    const includeThread = pending.some(e => e.kind === 'thread');
    const postIds = pending.filter(e => e.kind === 'post').map(e => e.id);
    this.send(this.uiLocale, includeThread, postIds, false);
  }

  /** Anything pending for longer than the cap stops being a spinner. */
  private expireTimeouts(): void {
    const now = Date.now();
    let changed = false;
    const next = { ...this.entries() };
    Object.entries(next).forEach(([key, entry]) => {
      if (entry.status === 'pending' && entry.pendingSince && now - entry.pendingSince > POLL_MAX_MS) {
        next[key] = { ...entry, status: 'timeout', pendingSince: undefined, showing: false };
        changed = true;
      }
    });
    if (changed) this.entries.set(next);
  }

  private pendingEntries(): CommunityTranslationEntry[] {
    return Object.values(this.entries()).filter(e => e.status === 'pending');
  }

  private cancelPoll(): void {
    if (this.pollHandle) {
      clearTimeout(this.pollHandle);
      this.pollHandle = null;
    }
    this.pollAttempt = 0;
  }

  // --- Reactions to outside changes -----------------------------------------------

  /** The UI language changed: everything held is for the old target language. */
  private onLanguageChanged(): void {
    this.cancelPoll();
    this.entries.set({});
    this.requestAuto();
  }

  private onModeChanged(sourceLocale: string, mode: string): void {
    if (mode === 'never') {
      // Fold every element of that language back to its original. The cache on
      // the server is untouched, so switching back costs nothing.
      this.entries.update(map => {
        const next = { ...map };
        Object.entries(next).forEach(([key, entry]) => {
          if (communitySameLocale(entry.sourceLocale, sourceLocale)) {
            next[key] = { ...entry, showing: false, requested: false };
          }
        });
        return next;
      });
      return;
    }
    if (mode === 'always') {
      const targets = Array.from(this.targets.values()).filter(
        t => communitySameLocale(t.sourceLocale, sourceLocale) && this.entryFor(t.kind, t.id)?.status !== 'pending'
      );
      if (targets.length) this.request(targets, false);
    }
  }

  private reset(): void {
    this.cancelPoll();
    this.targets.clear();
    this.entries.set({});
  }
}
